// Rascunho NÃO SALVO do Orçamento (Rafael, 08/10/2026) — regras puras (sem React, sem storage).
//
// O operador preenche o orçamento por muito tempo; se sair sem Salvar, perdia tudo. A tela guarda
// um rascunho (navegador + servidor) por USUÁRIO + EVENTO + ORÇAMENTO até ele Salvar ou Descartar.
//
// - chave: e<evento>:o<orçamento> | e<evento>:novo | e<evento>:copia-<origem> (o usuário vai à parte).
// - payload: só o que vai no Salvar (cabeçalho editável + itens + colunas adicionais). O que é do
//   servidor (aprovações, adiantamentos, totais da API, datas de gravação) fica de fora: no
//   Restaurar continua valendo o que acabou de vir da API.
// - assinatura: texto estável do payload, sem os campos calculados pela tela, para comparar
//   "o que está na tela" com a base carregada e com o rascunho.

export const VERSAO_PAYLOAD = 1;

// Parte 3 (19/10): o mesmo texto do aviso da tela — rascunho NÃO é o orçamento salvo.
export const MENSAGEM_SAIR_SEM_SALVAR = 'As alterações deste orçamento NÃO foram salvas: estão só no rascunho e não valem '
    + 'no orçamento. Para gravar, fique na tela e clique em Salvar. Sair mesmo assim?';

// Parte 3 (Rafael, 16/10): rascunho no servidor a cada 15 s (só se mudou desde o último envio);
// se falhar, nova tentativa em 30 s e depois a cada 60 s. A cópia do navegador segue a cada 2 s.
export const INTERVALO_RASCUNHO_SERVIDOR_MS = 15000;
export const TEMPO_LIMITE_RASCUNHO_MS = 30000;

export function esperaNovaTentativaMs(falhas) {
    return falhas <= 1 ? 30000 : 60000;
}

/** Erro do envio do rascunho → { status, semConexao, mensagem } (status 0 = sem resposta do servidor). */
export function erroDoEnvioRascunho(error) {
    const status = (error && error.response && error.response.status) || 0;
    const doServidor = error && error.response && error.response.data && error.response.data.error;
    return {
        status,
        semConexao: status === 0,
        mensagem: typeof doServidor === 'string' && doServidor.trim() !== '' ? doServidor.trim() : null,
    };
}

/**
 * Aviso fixo na tela enquanto só o rascunho tem as alterações (Rafael, 16/10: "sem parecer salvo").
 * erro = { semConexao, esperaMs, tentando } | null. Retorna null quando não há o que avisar.
 */
export function textoAvisoRascunho({ sujo, guardadoEm, erro, salvando } = {}) {
    if (salvando && (sujo || erro)) {
        return { tom: 'warn', titulo: 'Salvando o orçamento…', texto: 'Aguarde a confirmação antes de sair da tela.', botaoSalvar: false };
    }
    if (erro) {
        const quando = erro.tentando ? 'tentando de novo agora…' : `tentando de novo em ${Math.round((erro.esperaMs || 30000) / 1000)} s.`;
        const causa = erro.semConexao ? ' (sem conexão com o servidor)' : '';
        return {
            tom: 'danger',
            titulo: `Erro ao salvar o rascunho${causa}, ${quando}`,
            texto: 'Clique em Salvar para não perder os dados.',
            botaoSalvar: true,
        };
    }
    if (!sujo) return null;
    const hora = formatarHoraRascunho(guardadoEm);
    return {
        tom: 'warn',
        titulo: 'Alterações NÃO salvas',
        texto: hora
            ? `Estão só no rascunho (guardado às ${hora}) e ainda não valem no orçamento. Clique em Salvar para gravar.`
            : 'Ainda não valem no orçamento. Clique em Salvar para gravar.',
        botaoSalvar: true,
    };
}

/** Sessão da tela (letras e números): identifica os envios desta tela para o servidor. */
export function novaSessaoTela() {
    const letras = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let sessao = '';
    for (let i = 0; i < 12; i++) sessao += letras.charAt(Math.floor(Math.random() * letras.length));
    return sessao;
}

/**
 * Resposta de um envio de rascunho que saiu ANTES de um Salvar/Descartar e voltou DEPOIS, gravado
 * no servidor (não ignorado): ele recriou um rascunho velho. 'apagar' = apaga a chave do envio;
 * 'reenviar' = a tela já tem alteração nova na mesma chave, manda a versão atual por cima.
 */
export function acaoAposEnvioAtrasado({ chaveEnvio, chaveAtual, sujo }) {
    if (!chaveEnvio) return 'nada';
    if (sujo && chaveEnvio === chaveAtual) return 'reenviar';
    return 'apagar';
}

// Mudança de dado até este tempo depois de um clique/tecla do operador conta como edição dele.
// Fora disso é recálculo automático da tela (regras de FEE chegando, ordenação, colunas adicionais).
export const JANELA_INTERACAO_MS = 2500;

// Cabeçalho que é do servidor (não volta no Salvar ou é ignorado por ele): fica fora do rascunho.
const CAMPOS_SERVIDOR = new Set([
    'bancoDados', 'empresa', 'created_at', 'updated_at', 'valorTotal',
    'valorAdiantamento', 'saldoAdiantamento', 'arquivoReciboAdiantamento', 'arquivo_recibo_adiantamento',
    'recibos_adiantamento', 'adiantamento_detalhes', 'itensComAdiantamento', 'adiantamentosDoOrcamento',
    'adiantamentosPorItem', 'valorTotalEtapaVigente', 'colunasNegociadoAdicionais', 'orcamentoproduto',
    'enviadoFaturamento', 'emailsFaturamento', 'fechamento_exibicao',
    // D-0052 parte 4b: versão do último Salvar. Restaurar um rascunho mantém a versão que acabou de
    // vir da API (senão o Salvar seguinte levaria uma base velha e daria 409 para sempre).
    'chave_salvar', 'salvo_por', 'salvo_em',
]);
const PREFIXOS_SERVIDOR = [
    'dataAprovacao', 'aprovado', 'dataReprovacao', 'reprovado', 'motivoReprovacao',
    'remessa_estimativa', 'fechamento_real',
];
const REGEX_TOTAL_BRL = /^total.*BRL$/;

// Calculados pela própria tela a cada recálculo: não entram na assinatura.
const CAMPOS_CALCULADOS_CABECALHO = new Set([
    'valorTotalPreEvento', 'valorTotalNegociado', 'valorTotalFechamento',
    'impostoPlanilhaPreEvento', 'impostoPlanilhaNegociado', 'impostoPlanilhaFechamento',
]);
const CAMPOS_CALCULADOS_QUALQUER_NIVEL = new Set(['fechamento_exibicao']);

const CAMPOS_DATA = new Set(['data', 'pagamentovencimento']);
const CAMPOS_SO_ID = new Set(['produto', 'cliente']);

export function campoDoServidor(chave) {
    if (CAMPOS_SERVIDOR.has(chave)) return true;
    if (REGEX_TOTAL_BRL.test(chave)) return true;
    return PREFIXOS_SERVIDOR.some((prefixo) => chave.indexOf(prefixo) === 0);
}

export function idDoEvento(evento) {
    const bruto = evento && typeof evento === 'object' ? evento.id : evento;
    const texto = bruto === null || bruto === undefined ? '' : String(bruto).trim();
    return /^\d{1,20}$/.test(texto) ? texto : '0';
}

export function buildChave({ eventoId, orcamentoId, copiar } = {}) {
    const evento = idDoEvento(eventoId);
    const orcamento = orcamentoId === null || orcamentoId === undefined ? '' : String(orcamentoId).trim();
    if (/^\d{1,20}$/.test(orcamento)) {
        return copiar ? `e${evento}:copia-${orcamento}` : `e${evento}:o${orcamento}`;
    }
    return `e${evento}:novo`;
}

const semCamposCalculados = (valor) => {
    if (Array.isArray(valor)) return valor.map(semCamposCalculados);
    if (!valor || typeof valor !== 'object') return valor;
    const limpo = {};
    Object.keys(valor).forEach((chave) => {
        if (CAMPOS_CALCULADOS_QUALQUER_NIVEL.has(chave)) return;
        limpo[chave] = semCamposCalculados(valor[chave]);
    });
    return limpo;
};

// Colunas adicionais do Negociado: só a definição; `itens` é remontado no Salvar a partir dos subitens.
const colunasSemItens = (colunas) => (Array.isArray(colunas) ? colunas : [])
    .map((coluna) => {
        if (!coluna || typeof coluna !== 'object') return coluna;
        const { itens, ...resto } = coluna; // eslint-disable-line no-unused-vars
        return resto;
    });

/** O que vai para o rascunho: cópia JSON (sem referência ao estado) do que o Salvar envia. */
export function montarPayloadRascunho(dados, colunasNegociadoAdicionais) {
    const origem = dados && typeof dados === 'object' ? dados : {};
    const cabecalho = {};
    Object.keys(origem).forEach((chave) => {
        if (chave === 'orcamentoitem' || campoDoServidor(chave)) return;
        cabecalho[chave] = origem[chave];
    });
    cabecalho.orcamentoitem = Array.isArray(origem.orcamentoitem) ? origem.orcamentoitem : [];
    return JSON.parse(JSON.stringify({
        v: VERSAO_PAYLOAD,
        dados: semCamposCalculados(cabecalho),
        colunasNegociadoAdicionais: colunasSemItens(colunasNegociadoAdicionais),
    }));
}

// DD/MM/AAAA (tela) e AAAA-MM-DD[ hh:mm:ss] (depois do Salvar) são a mesma data.
export function normalizarData(valor) {
    if (typeof valor !== 'string') return valor;
    const br = valor.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (br) return `${br[3]}-${br[2]}-${br[1]}`;
    const iso = valor.match(/^(\d{4}-\d{2}-\d{2})/);
    return iso ? iso[1] : valor;
}

function serializarEstavel(valor, chavePai, nivel) {
    if (valor === undefined || typeof valor === 'function') return undefined;
    if (valor === null || typeof valor !== 'object') {
        const normalizado = CAMPOS_DATA.has(chavePai) ? normalizarData(valor) : valor;
        return JSON.stringify(normalizado);
    }
    if (Array.isArray(valor)) {
        return '[' + valor.map((item) => {
            const texto = serializarEstavel(item, null, nivel + 1);
            return texto === undefined ? 'null' : texto;
        }).join(',') + ']';
    }
    // Produto/cliente são cadastro (preço, faixas, config mudam lá): para "o operador mexeu?" basta qual é.
    if (CAMPOS_SO_ID.has(chavePai) && valor.id !== undefined && valor.id !== null) {
        return JSON.stringify({ id: String(valor.id) });
    }
    const partes = [];
    Object.keys(valor).sort().forEach((chave) => {
        if (CAMPOS_CALCULADOS_QUALQUER_NIVEL.has(chave)) return;
        // campo do servidor que ficou num rascunho antigo (ex.: chave_salvar, até 23/10) não conta
        if (nivel === 1 && (CAMPOS_CALCULADOS_CABECALHO.has(chave) || campoDoServidor(chave))) return;
        const texto = serializarEstavel(valor[chave], chave, nivel + 1);
        if (texto !== undefined) partes.push(JSON.stringify(chave) + ':' + texto);
    });
    return '{' + partes.join(',') + '}';
}

/** Assinatura estável de um payload (montarPayloadRascunho). Mesma tela = mesmo texto. */
export function assinaturaRascunho(payload) {
    const p = payload && typeof payload === 'object' ? payload : {};
    // nível 0 = envelope; o cabeçalho do orçamento é o nível 1 (dentro de "dados")
    return serializarEstavel({ dados: p.dados || {}, colunas: p.colunasNegociadoAdicionais || [] }, null, 0);
}

export function assinaturaDosDados(dados, colunasNegociadoAdicionais) {
    return assinaturaRascunho(montarPayloadRascunho(dados, colunasNegociadoAdicionais));
}

/**
 * Decide, a cada mudança de dados, se a tela está "suja" e qual é a base.
 * - antes de pronto (carregando, conferindo/perguntando sobre rascunho): a base acompanha a tela;
 * - igual à base: limpo;
 * - já suja: continua suja;
 * - mudou logo depois de interação do operador: suja;
 * - mudou sozinha (recálculo automático): a base acompanha.
 */
export function decidirEstadoRascunho({ pronto, sujo, assinatura, baseline, ultimaInteracaoEm, agora, janelaMs = JANELA_INTERACAO_MS }) {
    if (!pronto) return { sujo: false, baseline: assinatura };
    if (assinatura === baseline) return { sujo: false, baseline };
    if (sujo) return { sujo: true, baseline };
    if (ultimaInteracaoEm && agora - ultimaInteracaoEm >= 0 && agora - ultimaInteracaoEm <= janelaMs) {
        return { sujo: true, baseline };
    }
    return { sujo: false, baseline: assinatura };
}

const instante = (valor) => {
    if (!valor) return NaN;
    const ms = Date.parse(valor);
    return Number.isFinite(ms) ? ms : NaN;
};

/** Local (navegador) × servidor: vale o mais recente pelo salvo_em; sem data válida perde. */
export function escolherRascunhoMaisRecente(local, servidor) {
    const candidatos = [local, servidor].filter((r) => r && typeof r.payload === 'string' && r.payload !== '');
    if (candidatos.length === 0) return null;
    return candidatos.reduce((melhor, atual) => {
        const a = instante(atual.salvo_em);
        const m = instante(melhor.salvo_em);
        if (!Number.isFinite(m)) return atual;
        if (!Number.isFinite(a)) return melhor;
        return a > m ? atual : melhor;
    });
}

/** O orçamento foi salvo (por alguém) depois que o rascunho começou? */
export function rascunhoDesatualizado(baseAtualizadoEm, atualizadoEmAgora) {
    const base = instante(baseAtualizadoEm);
    const agora = instante(atualizadoEmAgora);
    if (!Number.isFinite(base) || !Number.isFinite(agora)) return false;
    return agora > base;
}

/**
 * Aplica o rascunho sobre o que acabou de vir da API: cabeçalho editável e itens do rascunho,
 * campos do servidor (aprovações, adiantamentos, updated_at…) como vieram agora.
 */
export function aplicarRascunho(dadosAtuais, payload) {
    const atuais = dadosAtuais && typeof dadosAtuais === 'object' ? dadosAtuais : {};
    const doRascunho = payload && payload.dados && typeof payload.dados === 'object' ? payload.dados : {};
    const resultado = { ...atuais };
    Object.keys(doRascunho).forEach((chave) => {
        if (chave === 'orcamentoitem' || campoDoServidor(chave)) return;
        // cliente vem do evento: o que acabou de carregar é mais novo (só o "novo" sem evento usa o do rascunho)
        if (chave === 'cliente' && atuais.cliente) return;
        resultado[chave] = doRascunho[chave];
    });
    resultado.orcamentoitem = Array.isArray(doRascunho.orcamentoitem) ? doRascunho.orcamentoitem : (atuais.orcamentoitem || []);
    const colunas = payload && Array.isArray(payload.colunasNegociadoAdicionais) ? payload.colunasNegociadoAdicionais : null;
    return { dados: resultado, colunasNegociadoAdicionais: colunas };
}

const doisDigitos = (n) => String(n).padStart(2, '0');

/** "08/10/2026 às 14:05" (hora local do navegador). */
export function formatarDataHoraRascunho(valor) {
    const ms = instante(valor);
    if (!Number.isFinite(ms)) return '';
    const d = new Date(ms);
    return `${doisDigitos(d.getDate())}/${doisDigitos(d.getMonth() + 1)}/${d.getFullYear()} às ${doisDigitos(d.getHours())}:${doisDigitos(d.getMinutes())}`;
}

/**
 * D-0052 parte 4a: "14:05" se for do mesmo dia de `agora`; senão "22/10 às 14:05" (com o ano se for
 * de outro ano). O rascunho de outro operador vale sem limite de tempo (Rafael, 23/10).
 */
export function quandoRascunhoDeOutro(valor, agora = new Date()) {
    const ms = instante(valor);
    if (!Number.isFinite(ms)) return '';
    const d = new Date(ms);
    const hora = `${doisDigitos(d.getHours())}:${doisDigitos(d.getMinutes())}`;
    if (d.getFullYear() === agora.getFullYear() && d.getMonth() === agora.getMonth() && d.getDate() === agora.getDate()) {
        return hora;
    }
    const ano = d.getFullYear() !== agora.getFullYear() ? `/${d.getFullYear()}` : '';
    return `${doisDigitos(d.getDate())}/${doisDigitos(d.getMonth() + 1)}${ano} às ${hora}`;
}

/**
 * D-0052 parte 4a (Rafael, 23/10): outro operador tem alteração não salva neste orçamento. Aviso de
 * quem abre: "O operador X está com uma versão não salva deste orçamento desde HH:MM. Peça para ele
 * salvar antes de você usar." outros = [{ nome, salvo_em }] da API. null quando não há o que avisar.
 */
export function textoAvisoOutroOperador(outros, agora = new Date()) {
    const lista = (Array.isArray(outros) ? outros : []).filter((o) => o && typeof o.nome === 'string' && o.nome.trim() !== '');
    if (lista.length === 0) return null;
    const desde = (o) => quandoRascunhoDeOutro(o.salvo_em, agora);
    if (lista.length === 1) {
        const quando = desde(lista[0]);
        return {
            titulo: 'Versão não salva de outro operador',
            texto: `O operador ${lista[0].nome.trim()} está com uma versão não salva deste orçamento${quando ? ` desde ${quando}` : ''}. `
                + 'Peça para ele salvar antes de você usar.',
        };
    }
    const nomes = lista.map((o) => {
        const quando = desde(o);
        return `${o.nome.trim()}${quando ? ` (desde ${quando})` : ''}`;
    });
    return {
        titulo: 'Versões não salvas de outros operadores',
        texto: `Os operadores ${nomes.slice(0, -1).join(', ')} e ${nomes[nomes.length - 1]} estão com versões não salvas deste orçamento. `
            + 'Peça para eles salvarem antes de você usar.',
    };
}

/** "14:05" */
export function formatarHoraRascunho(valor) {
    const ms = instante(valor);
    if (!Number.isFinite(ms)) return '';
    const d = new Date(ms);
    return `${doisDigitos(d.getHours())}:${doisDigitos(d.getMinutes())}`;
}
